// @flow
import React, { type ElementRef, Component, Fragment } from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import { UnmountClosed } from 'react-collapse';
import {
  Field,
  type FormProps,
  getFormMeta,
  getFormSyncErrors,
  reduxForm,
  formValueSelector,
} from 'redux-form';

import type { Dispatch, Role, User } from 'state/types';
import localizedStrings from 'util/localizedStrings';
import FormField from 'view/Forms/FormField';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import { createUser, updateUser } from 'state/commands/userCommands';
import { getRoles, getUsers } from 'state/selectors';
import submit from './submitUser';
import {
  ADMIN_USER_NAME,
  CREATE_USER_FORM,
  USER_DETAILS_FORM,
} from 'util/constants';
import CheckRadio from 'view/Forms/BulmaCheckRadio';
import { getLocalizedRoleName } from 'util/roleUtils';
import { isEmailLegal } from 'util/emailUtils';

type Props = {
  createUser: typeof createUser,
  formErrors: Object,
  form: string,
  intl: IntlShape,
  isCreate: boolean,
  isToken: boolean,
  meta: Object,
  roles: Role[],
  successMessage: string,
  updateUser: typeof updateUser,
  user: User,
  users: User[],
} & FormProps;

class UserForm extends Component<Props> {
  initializeFields: Function;
  handleAPIToggle: Function;
  accountField: ?ElementRef<typeof Field>;

  constructor() {
    super();
    this.initializeFields = this.initializeFields.bind(this);
    this.handleAPIToggle = this.handleAPIToggle.bind(this);
  }

  fieldStyle(fieldName) {
    const { formErrors, meta } = this.props;
    const hasError =
      formErrors[fieldName] && meta[fieldName] && meta[fieldName].touched;
    return `input ${hasError && 'is-danger'}`;
  }

  componentDidMount() {
    const { user } = this.props;
    this.initializeFields(user);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.user !== nextProps.user) {
      this.initializeFields(nextProps.user);
    }
  }

  initializeFields(user: User) {
    const { initialize } = this.props;
    initialize(user);
  }

  handleAPIToggle() {
    if (this.accountField) {
      const inputElement = findDOMNode(this.accountField);
      inputElement instanceof HTMLInputElement && inputElement.focus();
    }
  }

  render() {
    const {
      addUserToAllCatalogs,
      formErrors,
      fromLDAP,
      handleSubmit,
      intl: { formatDate, formatMessage, formatTime },
      intl,
      invalid,
      isCreate,
      isToken,
      pristine,
      reset,
      roles,
      submitting,
      user,
    } = this.props;

    const isFullAdmin = user.name === ADMIN_USER_NAME;
    const shortNameStyle = this.fieldStyle('name');
    const passwordStyle = this.fieldStyle('password');
    const createdValue =
      user.creationDate &&
      `${formatDate(user.creationDate)} ${formatTime(user.creationDate)}`;
    const modifiedValue =
      user.lastModifiedDate &&
      `${formatDate(user.lastModifiedDate)} ${formatTime(
        user.lastModifiedDate
      )}`;

    return (
      <div
        className={`portfolio-form-container ${
          isCreate ? 'page' : 'is-textured'
        }`}
        data-test-name="general-users-page"
      >
        <div className={`${!isCreate ? 'box' : ''}`}>
          <form onSubmit={handleSubmit(submit)} style={{ maxWidth: '700px' }}>
            <FormField
              label={formatMessage(localizedStrings.USER_ACCOUNT)}
              labelClassName="is-medium"
              readonly={fromLDAP || isFullAdmin}
              value={user.name}
              dataTestName="username"
            >
              <Field
                className={`is-medium ${shortNameStyle}`}
                component="input"
                id={isCreate ? 'name-create' : 'name'}
                name="name"
                type="text"
                ref={ref => {
                  this.accountField = ref;
                }}
                withRef
              />
            </FormField>

            {isCreate && (
              <div className="field" style={{ marginLeft: '185px' }}>
                <Field
                  component={CheckRadio}
                  type="checkbox"
                  className="switch is-rounded"
                  name="isToken"
                  id="isToken"
                  label={formatMessage(localizedStrings.API_TOKEN_CREATE)}
                  onChange={this.handleAPIToggle}
                />
              </div>
            )}

            {!isCreate && !isToken && !isFullAdmin && (
              <div className="field is-horizontal" key="authType">
                <div className="field-label is-normal">
                  <label className="label">
                    <FormattedMessage {...localizedStrings.ACCOUNT_TYPE_LDAP} />
                  </label>
                </div>
                <div className="field-body">
                  <div className="control">
                    <div className="field">
                      <Field
                        component={CheckRadio}
                        id="authType-checkbox"
                        name="fromLDAP"
                        type="checkbox"
                      />
                      <div className="vertical-spacer-small" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <UnmountClosed
              isOpened={!isToken}
              springConfig={{ stiffness: 250, damping: 25 }}
            >
              {!fromLDAP && (
                <FormField label={formatMessage(localizedStrings.PASSWORD)}>
                  <Field
                    autoComplete="new-password"
                    className={passwordStyle}
                    component="input"
                    id="password"
                    name="password"
                    placeholder={isCreate ? '' : '************************'}
                    type="password"
                  />
                </FormField>
              )}

              <FormField
                label={formatMessage(localizedStrings.FULL_NAME)}
                readonly={fromLDAP}
                value={user.longName}
              >
                <Field
                  className={`input ${formErrors.longName ? 'is-danger' : ''}`}
                  component="input"
                  id="longName"
                  name="longName"
                  type="text"
                />
              </FormField>

              <FormField
                label={formatMessage(localizedStrings.EMAIL_ADDRESS)}
                readonly={fromLDAP}
                value={user.email}
              >
                <Field
                  className={`input ${formErrors.email ? 'is-danger' : ''}`}
                  component="input"
                  id="email"
                  name="email"
                  type="text"
                  style={{ marginBottom: `${!isCreate ? '0.75rem' : ''}` }}
                />
              </FormField>
            </UnmountClosed>

            {isToken && !isCreate && (
              <FormField
                label={formatMessage(localizedStrings.TOKEN)}
                readonly
                value={user.token}
                data-test-name="token"
              />
            )}

            <FormField label={formatMessage(localizedStrings.NOTES)}>
              <Field
                className={`textarea ${formErrors.notes ? 'is-danger' : ''}`}
                component="textarea"
                id={isCreate ? 'notes-create' : 'notes'}
                name="notes"
                type="textarea"
              />
            </FormField>

            {isCreate && (
              <div style={{ marginLeft: '185px' }}>
                <div className="field">
                  <Field
                    component={CheckRadio}
                    id="addUserToAllCatalogs"
                    data-test-name="add-user-to-all-catalogs"
                    label={formatMessage(
                      localizedStrings.USER_NEW_USER_ADD_USER_TO_ALL_CATALOGS
                    )}
                    name="addUserToAllCatalogs"
                    type="checkbox"
                  />
                </div>

                <div className="field is-indented">
                  <div className="select">
                    <Field
                      component="select"
                      disabled={!addUserToAllCatalogs}
                      id="roleName"
                      name="roleName"
                    >
                      {roles.map(role => (
                        <option
                          key={role.name}
                          value={role.name}
                          data-test-name={'role-name-' + role.name}
                        >
                          {getLocalizedRoleName(intl, role.name)}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
                <div className="is-size-7 is-indented">
                  <FormattedMessage
                    {...localizedStrings.USER_NEW_USER_INFO_MSG}
                  />
                </div>
              </div>
            )}

            {!isCreate && fromLDAP && (
              <FormField
                label="LDAP DN"
                readonly
                value={user.ldapSourceDN}
                data-test-name="sourceDn"
              />
            )}

            {!isCreate && (
              <Fragment>
                <FormField
                  label={formatMessage(localizedStrings.CREATED_ON_DATE)}
                  readonly
                  value={createdValue}
                  data-test-name="dateCreated"
                />

                <FormField
                  label={formatMessage(localizedStrings.LAST_MODIFIED_DATE)}
                  readonly
                  value={modifiedValue}
                  data-test-name="dateModified"
                />

                <div className="field is-grouped is-pulled-right">
                  <div className="control">
                    <button
                      className="button is-dark is-outlined"
                      disabled={pristine | submitting}
                      onClick={reset}
                      type="button"
                      data-test-name="revert"
                    >
                      <FormattedMessage {...localizedStrings.REVERT} />
                    </button>
                  </div>
                  <div className="control">
                    <button
                      className={`button is-primary ${
                        submitting ? 'is-loading' : ''
                      }`}
                      type="submit"
                      disabled={pristine | invalid | submitting}
                      data-test-name="submit"
                    >
                      <FormattedMessage {...localizedStrings.APPLY} />
                    </button>
                  </div>
                </div>
                <div className="vertical-spacer-xlarge" />
              </Fragment>
            )}
          </form>
        </div>
      </div>
    );
  }
}

function validate(data: Object, props: Props) {
  const errors = {};
  const maxFieldLen = 255;

  function userExists(user: User) {
    return props.users.some(
      u =>
        // Case insensitive comparison
        u.name.localeCompare(user.name, undefined, {
          sensitivity: 'accent',
          usage: 'search',
        }) === 0 && u.uuid !== user.uuid
    );
  }

  if (!data.name || data.name.length > maxFieldLen || userExists(data)) {
    errors.name = 'Invalid';
  }

  if (
    data.email &&
    (data.email.length > maxFieldLen || !isEmailLegal(String(data.email)))
  ) {
    errors.email = 'Invalid';
  }

  if (data.password && data.password.length > maxFieldLen) {
    errors.password = 'Invalid';
  }

  if (props.isCreate && !data.password) {
    errors.password = 'Invalid';
  }

  if (data.longName && data.longName.length > maxFieldLen) {
    errors.longName = 'Invalid';
  }

  if (data.notes && data.notes.length > maxFieldLen) {
    errors.notes = 'Invalid';
  }

  return errors;
}

const createUserFormIntl = reduxForm({
  form: CREATE_USER_FORM,
  onSubmit: submit,
  validate,
})(injectIntl(UserForm));

const createUserFormSelector = formValueSelector(CREATE_USER_FORM);
const CreateUserForm = connect(
  state => ({
    addUserToAllCatalogs: createUserFormSelector(state, 'addUserToAllCatalogs'),
    formErrors: getFormSyncErrors(CREATE_USER_FORM)(state),
    meta: getFormMeta(CREATE_USER_FORM)(state),
    users: getUsers(state),
    isToken: createUserFormSelector(state, 'isToken'),
    roles: getRoles(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      createUser,
    })
)(createUserFormIntl);

const editUserFormIntl = reduxForm({
  form: USER_DETAILS_FORM,
  onSubmit: submit,
  validate,
})(injectIntl(UserForm));

const editUserFormSelector = formValueSelector(USER_DETAILS_FORM);
const EditUserForm = connect(
  state => ({
    formErrors: getFormSyncErrors(USER_DETAILS_FORM)(state),
    meta: getFormMeta(CREATE_USER_FORM)(state),
    users: getUsers(state),
    isToken: editUserFormSelector(state, 'isToken'),
    fromLDAP: editUserFormSelector(state, 'fromLDAP'),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      updateUser,
    })
)(editUserFormIntl);

export { CreateUserForm, EditUserForm };



// WEBPACK FOOTER //
// ./src/view/Pages/UsersPage/UserForm.js